/**
 * Diálogo «Compartir» de un reporte del Creador: personas y grupos con acceso (Vista/Edición),
 * agregar, cambiar y quitar, más copiar el link.
 * Lee de /api/creator y escribe con los endpoints de /admin que ya validan permisos y notifican;
 * cada control se desactiva si al usuario le falta el permiso del sistema correspondiente.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ink, studio, SearchField, StudioTabs } from '../studioUi';

const APP_URL = 'https://reportes.centralmedia.mx/';
const ACCESS_OPTIONS = [
    { value: 'view', label: 'Vista' },
    { value: 'edit', label: 'Edición' },
];

/** Link directo al reporte: solo abre a quien tenga acceso. */
export function reportShareLink(reportId) {
    return `${APP_URL}?reportId=${encodeURIComponent(reportId)}`;
}

function AccessSelect({ value, disabled, title, onChange }) {
    return (
        <select
            value={value}
            disabled={disabled}
            title={title}
            onChange={(e) => onChange(e.target.value)}
            style={{ ...studio.controlCompact, width: 112, opacity: disabled ? 0.6 : 1 }}
        >
            {ACCESS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
    );
}

function Avatar({ name, photoURL }) {
    const initials = String(name || '?').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
    return photoURL
        ? <img src={photoURL} alt="" style={styles.avatar} />
        : <span style={{ ...styles.avatar, ...styles.avatarText }}>{initials || '?'}</span>;
}

const ShareReportDialog = ({ open, report, onClose, onChanged, fetchWithAuth, API_URL }) => {
    const [tab, setTab] = useState('users');
    const [sharing, setSharing] = useState({ users: [], groups: [] });
    const [targets, setTargets] = useState({ users: [], groups: [] });
    const [caps, setCaps] = useState(report?.capabilities || {});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busyKey, setBusyKey] = useState('');
    const [query, setQuery] = useState('');
    const [newAccess, setNewAccess] = useState('view');

    const canSeeAccess = Boolean(caps.canSeeAccess);
    const canAddUsers = Boolean(caps.canShareUsers);
    const canAddGroups = Boolean(caps.canShareGroups);

    const load = useCallback(async () => {
        if (!report?.id) return;
        setLoading(true);
        setError('');
        try {
            if (report.capabilities?.canSeeAccess) {
                const res = await fetchWithAuth(`${API_URL}/api/creator/reports/${report.id}/sharing`);
                const data = await res.json();
                setSharing({ users: data.users || [], groups: data.groups || [] });
                if (data.capabilities) setCaps(data.capabilities);
            }
            if (report.capabilities?.canShareUsers || report.capabilities?.canShareGroups) {
                const res = await fetchWithAuth(`${API_URL}/api/creator/share-targets`);
                const data = await res.json();
                setTargets({ users: data.users || [], groups: data.groups || [] });
            }
        } catch (err) {
            setError(err.message || 'No se pudieron cargar los accesos.');
        } finally {
            setLoading(false);
        }
    }, [report?.id, report?.capabilities, fetchWithAuth, API_URL]);

    useEffect(() => {
        if (!open) return;
        setCaps(report?.capabilities || {});
        setTab('users');
        setQuery('');
        setNotice('');
        setSharing({ users: [], groups: [] });
        load();
    }, [open, report?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => { if (event.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const reportFields = useMemo(() => ({
        reporteId: report?.id,
        reporteNombre: report?.reporteNombre || '',
        cliente: report?.cliente || '',
        clientId: report?.clientId || '',
        proyecto: report?.proyecto || '',
        projectId: report?.projectId || '',
        dashboardUrl: report?.dashboardUrl || `custom:${report?.id}`,
    }), [report]);

    /** Ejecuta una mutación, recarga la lista y avisa a la biblioteca. */
    const mutate = async (key, request, okMessage) => {
        setBusyKey(key);
        setError('');
        setNotice('');
        try {
            await request();
            setNotice(okMessage);
            await load();
            if (onChanged) onChanged();
        } catch (err) {
            setError(err.message || 'No se pudo aplicar el cambio.');
        } finally {
            setBusyKey('');
        }
    };

    const send = (url, method, body) => fetchWithAuth(url, {
        method,
        silent: true,
        headers: { 'Content-Type': 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const addUser = (person) => mutate(`add-u-${person.uid}`,
        () => send(`${API_URL}/admin/assign-report`, 'POST', { ...reportFields, userId: person.uid, accessType: newAccess }),
        `${person.nombre || person.email} ahora tiene acceso de ${newAccess === 'edit' ? 'Edición' : 'Vista'}.`);
    const changeUser = (row, accessType) => mutate(`chg-u-${row.permissionId}`,
        () => send(`${API_URL}/admin/permissions/${row.permissionId}/access-type`, 'PUT', { accessType }),
        'Acceso actualizado.');
    const revokeUser = (row) => mutate(`del-u-${row.permissionId}`,
        () => send(`${API_URL}/admin/delete-permission/${row.permissionId}`, 'DELETE'),
        `Se quitó el acceso a ${row.nombre || row.email}.`);

    const addGroup = (group) => mutate(`add-g-${group.id}`,
        () => send(`${API_URL}/admin/groups/${group.id}/permissions`, 'POST', { ...reportFields, accessType: newAccess }),
        `El grupo ${group.name} ahora tiene acceso.`);
    const changeGroup = (row, accessType) => mutate(`chg-g-${row.permissionId}`,
        () => send(`${API_URL}/admin/groups/${row.groupId}/permissions/${row.permissionId}`, 'PUT', { accessType }),
        'Acceso actualizado.');
    const revokeGroup = (row) => mutate(`del-g-${row.permissionId}`,
        () => send(`${API_URL}/admin/groups/${row.groupId}/permissions/${report.id}`, 'DELETE'),
        `Se quitó el acceso al grupo ${row.name}.`);

    const copyLink = async () => {
        const link = reportShareLink(report.id);
        try {
            await navigator.clipboard.writeText(link);
            setNotice('Link copiado. Solo abre el reporte a quien tenga acceso.');
        } catch (err) {
            window.prompt('Copia el link del reporte:', link);
        }
    };

    if (!open || !report) return null;

    const needle = query.trim().toLowerCase();
    const sharedUserIds = new Set(sharing.users.map(row => row.userId));
    const sharedGroupIds = new Set(sharing.groups.map(row => row.groupId));
    const userCandidates = needle
        ? targets.users
            .filter(person => !sharedUserIds.has(person.uid))
            .filter(person => `${person.nombre} ${person.email} ${person.area}`.toLowerCase().includes(needle))
            .slice(0, 8)
        : [];
    const groupCandidates = targets.groups
        .filter(group => !sharedGroupIds.has(group.id))
        .filter(group => !needle || group.name.toLowerCase().includes(needle))
        .slice(0, 8);

    const noPermTitle = 'Tu rol no tiene permiso para esta acción.';
    const isUsers = tab === 'users';
    const rows = isUsers ? sharing.users : sharing.groups;
    const canAdd = isUsers ? canAddUsers : canAddGroups;
    const canChange = isUsers ? Boolean(caps.canChangeUserAccess) : canAddGroups;
    const canRevoke = isUsers ? Boolean(caps.canRevokeUser) : canAddGroups;

    return createPortal(
        <div style={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div role="dialog" aria-modal="true" aria-label={`Compartir ${report.reporteNombre}`} style={styles.dialog}>
                <div style={styles.head}>
                    <div style={{ minWidth: 0 }}>
                        <p style={studio.kicker}>Compartir</p>
                        <h3 style={styles.title}>{report.reporteNombre}</h3>
                        <p style={studio.hint}>{[report.cliente, report.proyecto].filter(Boolean).join(' · ')}</p>
                    </div>
                    <button type="button" onClick={onClose} style={styles.close} aria-label="Cerrar">×</button>
                </div>

                <div style={styles.linkRow}>
                    <span style={styles.linkText}>{reportShareLink(report.id)}</span>
                    <button type="button" onClick={copyLink} style={studio.ghostBtn}>Copiar link</button>
                </div>

                {!canSeeAccess ? (
                    <p style={{ ...studio.hint, padding: '8px 0' }}>
                        Tu rol no puede ver ni cambiar quién tiene acceso a este reporte. Puedes copiar el link;
                        solo lo abrirá quien ya tenga acceso.
                    </p>
                ) : (
                    <>
                        <StudioTabs
                            value={tab}
                            onChange={(next) => { setTab(next); setQuery(''); }}
                            items={[
                                { id: 'users', label: 'Personas', count: sharing.users.length },
                                { id: 'groups', label: 'Grupos', count: sharing.groups.length },
                            ]}
                        />

                        {canAdd ? (
                            <div style={styles.addRow}>
                                <SearchField
                                    value={query}
                                    onChange={setQuery}
                                    placeholder={isUsers ? 'Buscar persona por nombre o correo…' : 'Buscar grupo…'}
                                    style={{ marginBottom: 0, flex: 1 }}
                                />
                                <AccessSelect value={newAccess} onChange={setNewAccess} title="Acceso que se dará" />
                            </div>
                        ) : (
                            <p style={styles.lockedHint}>
                                {isUsers
                                    ? 'Para dar acceso a personas se necesita el permiso «Asignar permisos directos».'
                                    : 'Para dar acceso a grupos se necesita el permiso «Gestionar permisos de grupo».'}
                            </p>
                        )}

                        {canAdd && (isUsers ? userCandidates : (needle || groupCandidates.length ? groupCandidates : [])).length > 0 && (
                            <div style={styles.candidates}>
                                {(isUsers ? userCandidates : groupCandidates).map(item => (
                                    <button
                                        key={isUsers ? item.uid : item.id}
                                        type="button"
                                        disabled={Boolean(busyKey)}
                                        onClick={() => (isUsers ? addUser(item) : addGroup(item))}
                                        style={styles.candidate}
                                    >
                                        {isUsers && <Avatar name={item.nombre || item.email} photoURL={item.photoURL} />}
                                        <span style={styles.candidateText}>
                                            <strong style={styles.rowName}>{isUsers ? (item.nombre || item.email) : item.name}</strong>
                                            {isUsers && <span style={styles.rowMeta}>{[item.email, item.area].filter(Boolean).join(' · ')}</span>}
                                        </span>
                                        <span style={styles.addTag}>+ Dar acceso</span>
                                    </button>
                                ))}
                            </div>
                        )}
                        {canAdd && isUsers && needle && userCandidates.length === 0 && (
                            <p style={styles.lockedHint}>Nadie más coincide con «{query.trim()}».</p>
                        )}

                        <div style={styles.list}>
                            {loading && <p style={studio.hint}>Cargando accesos…</p>}
                            {!loading && rows.length === 0 && (
                                <p style={studio.hint}>
                                    {isUsers ? 'Nadie tiene acceso directo a este reporte.' : 'Ningún grupo tiene acceso a este reporte.'}
                                </p>
                            )}
                            {rows.map(row => {
                                const key = row.permissionId;
                                const rowBusy = busyKey.endsWith(key);
                                return (
                                    <div key={key} style={{ ...styles.row, opacity: rowBusy ? 0.55 : 1 }}>
                                        {isUsers
                                            ? <Avatar name={row.nombre || row.email} photoURL={row.photoURL} />
                                            : <span style={{ ...styles.avatar, ...styles.avatarText }}>G</span>}
                                        <span style={styles.candidateText}>
                                            <strong style={styles.rowName}>{isUsers ? (row.nombre || row.email || row.userId) : row.name}</strong>
                                            <span style={styles.rowMeta}>
                                                {isUsers ? row.email : `${row.members} miembro${row.members === 1 ? '' : 's'}`}
                                            </span>
                                        </span>
                                        <AccessSelect
                                            value={row.accessType}
                                            disabled={!canChange || Boolean(busyKey)}
                                            title={canChange ? 'Cambiar acceso' : noPermTitle}
                                            onChange={(value) => (isUsers ? changeUser(row, value) : changeGroup(row, value))}
                                        />
                                        <button
                                            type="button"
                                            disabled={!canRevoke || Boolean(busyKey)}
                                            title={canRevoke ? 'Quitar acceso' : noPermTitle}
                                            onClick={() => (isUsers ? revokeUser(row) : revokeGroup(row))}
                                            style={{ ...styles.revoke, opacity: canRevoke ? 1 : 0.4 }}
                                        >
                                            Quitar
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {error && <div style={styles.error}>{error}</div>}
                {notice && !error && <div style={styles.notice}>{notice}</div>}

                <div style={styles.foot}>
                    <span style={studio.hint}>Quien recibe acceso recibe una notificación y un correo.</span>
                    <button type="button" onClick={onClose} style={studio.primaryBtn}>Listo</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const styles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.38)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
    },
    dialog: {
        width: 'min(620px, 100%)',
        maxHeight: 'calc(100vh - 32px)',
        overflowY: 'auto',
        backgroundColor: ink.surface,
        borderRadius: 16,
        boxShadow: '0 20px 50px rgba(28, 29, 33, 0.25)',
        padding: '22px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        boxSizing: 'border-box',
    },
    head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
    title: { margin: '4px 0 2px', fontSize: '1.2rem', fontWeight: 600, color: ink.text, letterSpacing: '-0.02em' },
    close: {
        appearance: 'none',
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: 26,
        lineHeight: 1,
        color: ink.faint,
        cursor: 'pointer',
        padding: 4,
    },
    linkRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 8px 8px 12px',
        backgroundColor: ink.wash,
        borderRadius: 10,
    },
    linkText: {
        flex: 1,
        minWidth: 0,
        fontSize: '0.82rem',
        color: ink.muted,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    addRow: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
    lockedHint: { margin: 0, fontSize: '0.84rem', color: ink.faint },
    candidates: {
        display: 'flex',
        flexDirection: 'column',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
        overflow: 'hidden',
    },
    candidate: {
        appearance: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 12px',
        backgroundColor: ink.surface,
        borderWidth: 0,
        borderBottomWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
    },
    candidateText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 },
    addTag: { fontSize: '0.8rem', fontWeight: 600, color: ink.accentText, whiteSpace: 'nowrap' },
    list: { display: 'flex', flexDirection: 'column', gap: 4 },
    row: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px' },
    rowName: {
        fontSize: '0.9rem',
        fontWeight: 600,
        color: ink.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    rowMeta: {
        fontSize: '0.78rem',
        color: ink.faint,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    avatar: { width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
    avatarText: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ink.accentSoft,
        color: ink.accentText,
        fontSize: '0.75rem',
        fontWeight: 700,
    },
    revoke: {
        appearance: 'none',
        backgroundColor: 'transparent',
        border: 'none',
        color: ink.danger,
        fontWeight: 600,
        fontSize: '0.82rem',
        cursor: 'pointer',
        padding: '6px 4px',
        fontFamily: 'inherit',
    },
    error: { padding: '10px 12px', borderRadius: 10, backgroundColor: ink.dangerSoft, color: ink.danger, fontSize: '0.86rem' },
    notice: { padding: '10px 12px', borderRadius: 10, backgroundColor: ink.accentSoft, color: ink.accentText, fontSize: '0.86rem' },
    foot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
};

export default ShareReportDialog;
